import { withLifeErrorBoundary } from '@festina-life/components'
import type { Optional } from '@festina-life/core'
import {
  evaluateExpression,
  FPlusEvaluatingCodeEditor,
  FPlusViewerWithOverflowIndicator,
} from '@festina-life/fplus'
import { Button } from '@festina-life/ui/actions'
import { useLocalStorage, useTranslate } from '@festina-life/ui/hooks'
import { paperStyles, withSuspense } from '@festina-life/ui/libs'
import { Typography } from '@festina-life/ui/structure'
import { type ReactNode, useCallback, useState } from 'react'
import {
  Group,
  Panel,
  Separator,
  useDefaultLayout,
} from 'react-resizable-panels'

import { OVERFLOW_LINE_WIDTH } from '../../components/formattedFPlusViewer/FormattedFPlusViewer'
import { useNavigationParams } from '../../system/useNavigationParams'
import { useConfigurationLanguageServerInputInitializer } from '../../uiModels/fplus/useConfigurationLanguageServerInputInitializer'
import { useFPlusCodeEditorContext } from '../../uiModels/fplus/useFPlusCodeEditorContext'

type EvaluationResult = {
  result: ReactNode
  code: Optional<string>
  key: number
}

const ScratchpadSceneInternal = () => {
  const languageServerInputProvider =
    useConfigurationLanguageServerInputInitializer()
  const codeEditorInput = useFPlusCodeEditorContext({
    prefixFunctionDeclaration: null,
  })
  const translate = useTranslate()
  const [results, setResults] = useState<EvaluationResult[]>([])
  const { server, layer } = useNavigationParams()
  const [code, setCode] = useLocalStorage<string>(
    `scratchpad@${server}@${layer.layerId}`,
  )
  const { defaultLayout, onLayoutChanged } = useDefaultLayout({
    groupId: 'scratchpadPanel',
    storage: localStorage,
  })

  const evaluatePressed = useCallback(async () => {
    const evaluationResult = await evaluateExpression(
      languageServerInputProvider({ expressions: code }),
      (errorMessage) =>
        setResults([
          {
            result: `${translate('scratchpadScene.evaluation.error')} ${
              errorMessage
            }`,
            code,
            key: results.length + 1,
          },
          ...results,
        ]),
    )

    setResults([
      { result: evaluationResult as ReactNode, code, key: results.length + 1 },
      ...results,
    ])
  }, [languageServerInputProvider, translate, code, results, setResults])

  const clearResults = useCallback(() => {
    setResults([])
  }, [setResults])

  return (
    <div className="flex h-full flex-col gap-2 ">
      <div className="flex h-full grow flex-row">
        <Group
          defaultLayout={defaultLayout}
          onLayoutChanged={onLayoutChanged}
          orientation="horizontal"
        >
          <Panel
            id="templateMainPanel"
            className="size-full"
            minSize="10%"
            defaultSize="50%"
          >
            <FPlusEvaluatingCodeEditor
              editorInput={codeEditorInput}
              initialCode={code}
              onEvaluatePressed={evaluatePressed}
              onCodeChanged={setCode}
            />
          </Panel>
          <Separator className="w-1 dark:bg-slate-700 bg-slate-300" />
          <Panel
            id="templateDetailstest"
            className="size-full"
            minSize="10%"
            defaultSize="20%"
          >
            <div className="flex h-full grow flex-col gap-2">
              <div className="flex justify-between items-center p-2">
                <span>{translate('scratchpadScene.evaluation.header')}</span>
                <Button onClick={evaluatePressed}>
                  {translate('scratchpadScene.button.evaluate')}
                </Button>
              </div>

              <div className={paperStyles({ borders: ['bottom'] })} />
              <div className="h-full p-4">
                <div className="mb-4 flex">
                  <Typography
                    as="span"
                    size="xl"
                    className="m-auto w-full font-medium"
                  >
                    {translate('scratchpadScene.header.results')}
                  </Typography>
                  <Button onClick={clearResults}>
                    {translate('scratchpadScene.button.clear')}
                  </Button>
                </div>
                <div className="h-full overflow-scroll">
                  {results.map((result) => (
                    <div className="pb-4" key={result.key}>
                      <div className="max-h-[50px] min-h-[3em]">
                        <FPlusViewerWithOverflowIndicator
                          code={result.code ?? ''}
                          tooltipCode={result.code ?? ''}
                          alignment="left"
                          tooltipLineLength={OVERFLOW_LINE_WIDTH}
                        />
                      </div>
                      <p>{result.result}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Panel>
        </Group>
      </div>
    </div>
  )
}

export const ScratchpadScene = withSuspense(
  withLifeErrorBoundary(ScratchpadSceneInternal),
)
